import React, { useEffect, useMemo, useRef, useState } from 'react';
import { addcomponent, iconsPath } from './imagePath';
import { loadGoogleFontsBatch, updatePreviewDimention } from './UtilsFunction';
import { debounce } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import '../components/AddComponents/Styles/colorPickerOverrides.css';
import { SketchPicker } from 'react-color';
import ColorPickerModal from '../components/ui/ColorPickerModal';
import ReactDOM from 'react-dom';
import * as Three from 'three';
import store from '../redux/store';
import { updateCameraType, updateOpenModals } from '../redux/Slice/gridSlice';
import {
  update_full_window_size,
  update_preview_type,
  update_window_size_by_key,
} from '../redux/Slice/windowTypeSlice';
import usePreviewResize from '../Hooks/usePreviewResize';
import toast from 'react-hot-toast';
import { SimpleModal } from '../components/ui/NormalModal';
import { syncViewportCameraSwitch } from './ThreeJsUtils/viewportCameraSync';
import { downloadFullQualityMaterialMap } from './ThreeJsUtils/materialMaps';

export const CameraSwitcher = ({
  orthographicCamera,
  perspectiveCamera,
  currentCamera,
  controlsOrbit,
  renderer,
  scene,
  update,
  object,
  isAnimationMinimized,
  animationHeight = 250,
}) => {
  const dispatch = useDispatch();
  const { cameraType } = useSelector((state) => state?.assets_object);

  const [windowHeight, setWindowHeight] = React.useState(window.innerHeight);

  // Track window height
  React.useEffect(() => {
    const handleResize = () => setWindowHeight(window.innerHeight);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Camera update listener
  React.useEffect(() => {
    const handleCameraUpdate = () => {
      if (
        window.cameraProperties &&
        Array.isArray(window.cameraProperties) &&
        window.cameraProperties.length > 0
      ) {
        const publishedCameraType = window.cameraProperties[0].type;
        if (publishedCameraType) {
          const lowercaseType = publishedCameraType.toLowerCase();
          const currentReduxType = store.getState()?.assets_object?.cameraType;
          if (currentReduxType !== lowercaseType) {
            dispatch(updateCameraType(lowercaseType));
          }
        }
      }
    };

    window.addEventListener('cameraUpdated', handleCameraUpdate);
    return () => {
      window.removeEventListener('cameraUpdated', handleCameraUpdate);
    };
  }, [dispatch]);

  const isOrthographic = cameraType === 'orthographic';

  const switchCameraHandler = (value) => {
    const newType = value ? 'orthographic' : 'perspective';
    dispatch(updateCameraType(newType));

    const fromCamera = currentCamera;
    const newCamera = value ? orthographicCamera : perspectiveCamera;
    if (!fromCamera || !newCamera || !controlsOrbit) return;

    syncViewportCameraSwitch(
      fromCamera,
      newCamera,
      controlsOrbit.target,
      window.innerWidth,
      window.innerHeight
    );

    controlsOrbit.object = newCamera;
    controlsOrbit.update();
    renderer?.render(scene, newCamera);
    update(newCamera);
    localStorage.setItem('segmentedControlPage', window.location.pathname);
  };

  // Dynamic bottom position
  const getBottomPosition = () => {
    if (isAnimationMinimized) {
      return '48px'; // bottom-12
    } else {
      return `${animationHeight + 16}px`;
    }
  };

  // Dynamic horizontal position
  const getHorizontalPosition = () => {
    const heightRatio = Math.max(0, Math.min(1, (windowHeight - 300) / 500)); // normalize 300-800px

    if (isAnimationMinimized) {
      if (windowHeight < 500) {
        return { right: '254px', left: 'auto', transform: 'none' };
      } else {
        return { left: '50%', transform: 'translateX(-50%)' };
      }
    } else {
      const rightOffset = 266 - heightRatio * 5; // 260px → 60px
      return { right: `${rightOffset}px`, left: 'auto' };
    }
  };

  return (
    <div
      className={`fixed  shadow-lg flex bg-[#222428] rounded-full p-1 transition-all duration-300
        ${isAnimationMinimized ? 'h-13' : ''}`}
      style={{
        bottom: getBottomPosition(),
        ...getHorizontalPosition(),
      }}
    >
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => switchCameraHandler(true)}
        className={`transition-all duration-300 rounded-full outline-none border-0 focus:outline-none focus-visible:outline-none focus:ring-0 active:outline-none
          ${isAnimationMinimized
            ? 'h-[28px] min-w-[90px] text-[12px] px-3 font-normal'
            : 'h-[22px] min-w-[80px] text-[10px] px-2 font-normal'
          } ${isOrthographic
            ? 'bg-[#393A3D] text-white'
            : 'bg-transparent text-[#6D6D6D]'
          }`}
      >
        Orthographic
      </button>

      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => switchCameraHandler(false)}
        className={`transition-all duration-300 rounded-full outline-none border-0 focus:outline-none focus-visible:outline-none focus:ring-0 active:outline-none
          ${isAnimationMinimized
            ? 'h-[28px] min-w-[90px] text-[12px] px-3 font-normal'
            : 'h-[22px] min-w-[80px] text-[10px] px-2 font-normal'
          } ${!isOrthographic
            ? 'bg-[#393A3D] text-white'
            : 'bg-transparent text-[#6D6D6D]'
          }`}
      >
        Perspective
      </button>
    </div>
  );
};

export const ViewHelperComponent = ({
  isAnimationMinimized,
  animationHeight = 250,
}) => {
  const { window_type } = useSelector((state) => state.window);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);

  useEffect(() => {
    const handleResize = () => setWindowHeight(window.innerHeight);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getBottomPosition = () => {
    return isAnimationMinimized ? '100px' : `${animationHeight + 80}px`;
  };

  const getHorizontalPosition = () => {
    const heightRatio = Math.max(0, Math.min(1, (windowHeight - 300) / 500));
    if (isAnimationMinimized) {
      if (windowHeight < 500)
        return { right: '320px', left: 'auto', transform: 'none' };
      return { left: '50%', transform: 'translateX(-50%)' };
    } else {
      const rightOffset = 320 - heightRatio * 5;
      return {
        right: `${rightOffset}px`,
        left: 'auto',
        transform: 'translateY(32%)',
      };
    }
  };

  useEffect(() => {
    const helperCanvas = document.querySelector('#viewHelper canvas');
    if (helperCanvas) {
      helperCanvas.style.display = window_type === 'preview' ? 'none' : 'block';
    }
  }, [window_type]);

  return (
    <div
      id="viewHelper"
      className={`fixed rounded-full bg-[#222427] translate-y-[18px] transition-all duration-300 
        ${isAnimationMinimized ? 'w-[70px] h-[70px]' : 'w-[70px] h-[70px]'}
        ${window_type === 'preview' ? 'pointer-events-none opacity-0' : 'opacity-100'}`}
      style={{
        bottom: getBottomPosition(),
        ...getHorizontalPosition(),
      }}
    />
  );
};

export const EnhancedSlider = React.memo(
  ({
    label,
    value,
    onChange,
    min = 1,
    max = 100,
    step = 1,
    showTooltip = true,
    unit = '',
    disabled = false,
    maxEdit = false,
    toFix = 2,
    restrictInput = false,
    minLabel = '',
    maxLabel = '',
  }) => {
    const sliderRef = useRef(null);
    const isFocused = useRef(false);
    const [isDragging, setIsDragging] = useState(false);
    const [internalValue, setInternalValue] = useState(value);
    const [dynamicMax, setDynamicMax] = useState(max);
    const [showMaxWarning, setShowMaxWarning] = useState(false);

    const percentage = useMemo(() => {
      const denominator = dynamicMax - min;
      const percentageValue = Number.isFinite(denominator)
        ? ((internalValue - min) / denominator) * 100
        : 0;
      return Math.min(100, Math.max(0, percentageValue));
    }, [dynamicMax, internalValue, min]);

    useEffect(() => {
      if (isFocused.current) return;
      setInternalValue((prev) => (prev === value ? prev : value));
    }, [value]);

    useEffect(() => {
      const nextMax = value > max ? value : max;
      setDynamicMax((prev) => (prev === nextMax ? prev : nextMax));
    }, [value, max]);

    const debouncedChange = useMemo(() => {
      return debounce((val) => {
        onChange(val);
      }, 300);
    }, [onChange]);

    useEffect(() => {
      return () => {
        debouncedChange.cancel?.();
      };
    }, [debouncedChange]);

    const calculateValue = (clientX) => {
      if (!sliderRef.current) return;
      const rect = sliderRef.current.getBoundingClientRect();
      const relativeX = clientX - rect.left;
      const ratio = Math.max(0, Math.min(1, relativeX / rect.width));
      const newValue =
        Math.round((min + (dynamicMax - min) * ratio) / step) * step;
      return Math.min(dynamicMax, Math.max(min, newValue));
    };

    const handleUpdate = (val) => {
      setInternalValue(val);
      debouncedChange(val);
    };

    const handleClick = (e) => {
      if (disabled) return;
      const newValue = calculateValue(e.clientX);
      if (newValue !== undefined) handleUpdate(newValue);
    };

    const handleMouseDown = (e) => {
      if (disabled) return;
      e.preventDefault();
      setIsDragging(true);

      const updateValue = (moveEvent) => {
        const newValue = calculateValue(moveEvent.clientX);
        if (newValue !== undefined) handleUpdate(newValue);
      };

      const stopDragging = () => {
        setIsDragging(false);
        window.removeEventListener('mousemove', updateValue);
      };

      window.addEventListener('mousemove', updateValue);
      window.addEventListener('mouseup', stopDragging, { once: true });
    };

    const handleInputChange = (e) => {
      const inputValue = e.target.value;

      if (inputValue === '') {
        setInternalValue('');
        return;
      }

      if (inputValue === '-') {
        setInternalValue('-');
        return;
      }

      // Allow in-progress decimal typing: "0.", "0.0", "0.00", etc.
      // Store the raw string so the user can keep typing without the value being snapped.
      const isIncompleteDecimal = /^-?\d+\.\d*$/.test(inputValue);
      const numValue = parseFloat(inputValue);

      if (isNaN(numValue)) return;

      if (numValue > max) {
        setShowMaxWarning(true);
        setTimeout(() => setShowMaxWarning(false), 2000);
      }

      if (!maxEdit && numValue > max) {
        setInternalValue(max);
        debouncedChange(max);
        return;
      }

      // Keep raw string while typing so trailing zeros / dots are preserved
      setInternalValue(isIncompleteDecimal ? inputValue : numValue);

      if (maxEdit && numValue > dynamicMax) {
        setDynamicMax(numValue);
      }

      const clamped = maxEdit
        ? Math.max(min, numValue)
        : Math.min(max, Math.max(min, numValue));

      debouncedChange(clamped);
    };

    const handleInputBlur = () => {
      isFocused.current = false;
      const num = parseFloat(internalValue);
      if (internalValue === '' || isNaN(num)) {
        setInternalValue(value ?? min);
      } else {
        // Normalize to a clean number on blur
        const clamped = maxEdit
          ? Math.max(min, num)
          : Math.min(max, Math.max(min, num));
        setInternalValue(clamped);
      }
    };

    const formatValue = (val) => {
      if (val === '' || val === '-') return val;
      // If the value is already a string (raw in-progress input), return as-is
      if (typeof val === 'string') return val;
      if (typeof val === 'number' && val % 1 !== 0) {
        return val.toString().split('.')[1]?.length > toFix
          ? val.toFixed(toFix)
          : val.toString();
      }
      return val?.toString();
    };

    return (
      <div className="flex flex-col pb-2 relative">
        {/* ROW */}
        <div className="flex items-center gap-2">
          {label && <LabelHover label={label} />}

          <div className="flex-[16] flex items-center justify-start pl-2">
            <div
              ref={sliderRef}
              className={`${unit ? 'w-16' : 'w-20'} relative h-1 ${disabled ? 'bg-gray-600' : 'bg-[#2B2C2F]'
                } rounded-full mr-3`}
              onClick={handleClick}
            >
              <div className="absolute inset-0 flex items-center">
                <div
                  className={`w-3 h-3 rounded-full cursor-pointer absolute ${disabled ? 'bg-gray-500 cursor-not-allowed' : 'bg-gray-400'
                    }`}
                  style={{
                    left: `${percentage}%`,
                    transform: 'translateX(-50%)',
                  }}
                  onMouseDown={handleMouseDown}
                />
              </div>

              {showTooltip && isDragging && (
                <div
                  className="absolute bg-[#2B2C2F] border border-gray-700 text-white text-[10px] px-2 py-1 rounded"
                  style={{
                    left: `${percentage}%`,
                    top: '-30px',
                    transform: 'translateX(-50%)',
                  }}
                >
                  {formatValue(internalValue)}
                  {unit}
                </div>
              )}
            </div>

            {/* Min and Max Labels */}
            {(minLabel || maxLabel) && (
              <div
                className={`absolute ${unit ? 'w-16' : 'w-20'} flex justify-between text-[9px] text-gray-400 pointer-events-none `}
                style={{ top: '15px' }}
              >
                {minLabel && <span className="text-left">{minLabel}</span>}
                {maxLabel && <span className="text-right">{maxLabel}</span>}
              </div>
            )}

            <input
              type="number"
              min={min}
              max={maxEdit ? undefined : max}
              step={step}
              disabled={disabled}
              value={formatValue(internalValue)}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              onFocus={(e) => {
                isFocused.current = true;
                e.target.select();
              }}
              onKeyDown={(e) => {
                if (!restrictInput) return;

                const invalidChars = ['e', 'E', '+'];
                if (min >= 0) {
                  invalidChars.push('-');
                }
                if (invalidChars.includes(e.key)) {
                  e.preventDefault();
                  return;
                }

                // Handle keyboard arrows manually so precision isn't lost by the formatted string value
                if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                  e.preventDefault();

                  // Ensure keyboard step is large enough to cause a visible change in the UI
                  const displayStep = Math.max(
                    Number(step),
                    Math.pow(10, -toFix)
                  );

                  // Calculate precision to avoid floating point math issues
                  const precision =
                    displayStep.toString().split('.')[1]?.length || 0;

                  let newValue;
                  if (e.key === 'ArrowUp') {
                    newValue = Number(internalValue) + displayStep;
                    newValue = Math.min(maxEdit ? Infinity : max, newValue);
                  } else {
                    newValue = Number(internalValue) - displayStep;
                    newValue = Math.max(min, newValue);
                  }

                  // Apply precision rounding so the internal value snaps to the visible grid
                  newValue = parseFloat(newValue.toFixed(precision));

                  setInternalValue(newValue);
                  debouncedChange(newValue);
                }
              }}
              className={`w-9 h-5 bg-[#2B2C2F] border ${disabled
                  ? 'border-gray-500 text-gray-500'
                  : 'border-gray-700 text-white'
                } text-[10px] rounded px-[2px] text-center focus:outline-none `}
            />

            {unit && (
              <span className="ml-1 text-gray-400 text-[10px]">{unit}</span>
            )}
          </div>
        </div>

        {/* WARNING BELOW */}
        {showMaxWarning && (
          <div className="mt-1 text-[#EE2B2A] text-[10px] font-semibold px-2">
            {maxEdit
              ? `Recommended max is ${max}${unit}`
              : `Max limit is ${max}${unit}`}
          </div>
        )}
      </div>
    );
  }
);

export const LabelHover = React.memo(({ label, flex = 'flex-[10]' }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const shouldTruncate = label?.length > 13;
  const displayLabel = shouldTruncate ? `${label.slice(0, 10)}...` : label;

  return (
    <div
      className={`relative  max-w-[100px] ${flex}`}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <label className=" text-[#A6A6A6] text-[10px] mb-1 whitespace-nowrap overflow-hidden text-ellipsis">
        {displayLabel}
      </label>

      {shouldTruncate && showTooltip && (
        <div className="absolute z-10 -top-[150%] mt-1 left-0 bg-black text-white text-[10px] px-2 py-1 rounded shadow-lg whitespace-nowrap">
          {label}
        </div>
      )}
    </div>
  );
});

export const SimpleAssetTooltip = React.memo(
  ({ label, children, className = '', offsetLeft = 0 }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
    const elementRef = useRef(null);

    const handleMouseEnter = () => {
      if (elementRef.current) {
        // Check if text is actually truncated
        const textElement = elementRef.current.querySelector('span');
        if (textElement && textElement.scrollWidth > textElement.clientWidth) {
          const rect = elementRef.current.getBoundingClientRect();
          setTooltipPos({
            top: rect.bottom + 4,
            left: rect.left + offsetLeft,
          });
          setShowTooltip(true);
        }
      }
    };

    return (
      <>
        <div
          ref={elementRef}
          className={className}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={() => setShowTooltip(false)}
        >
          {children}
        </div>

        {showTooltip && (
          <div
            className="fixed z-50 bg-black text-white text-[10px] px-2 py-1 rounded shadow-lg whitespace-nowrap"
            style={{
              top: `${tooltipPos.top}px`,
              left: `${tooltipPos.left}px`,
            }}
            onMouseLeave={() => setShowTooltip(false)}
            onMouseEnter={() => setShowTooltip(true)}
          >
            {label}
          </div>
        )}
      </>
    );
  }
);

export const GridDropdown = ({
  label,
  value,
  onChange,
  options,
  className,
  menuClassName = 'scrollbar',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const ref = useRef();
  const dropdownRef = useRef();

  const selected = options?.find((opt) => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      // Find the closest parent that might constrain the dropdown (sidebar, scrollable div, etc.)
      const container =
        dropdownRef.current.closest('.scrollbar') ||
        dropdownRef.current.closest('.custom-scrollbar') ||
        dropdownRef.current.closest('.overflow-auto') ||
        dropdownRef.current.closest('.control-panel');

      const bottomBoundary = container
        ? container.getBoundingClientRect().bottom
        : window.innerHeight;

      const spaceBelow = bottomBoundary - rect.bottom;
      const dropdownHeight = 100; // Increased buffer for better visibility

      // Also check space above to make sure we don't just move the problem upwards
      if (spaceBelow < dropdownHeight && rect.top > dropdownHeight) {
        setOpenUp(true);
      } else {
        setOpenUp(false);
      }
    }
  }, [isOpen]);

  return (
    <div className="mb-2 flex items-center gap-2" ref={ref}>
      {label && <LabelHover label={label} />}
      <div className="relative flex-[16] text-[10px]" ref={dropdownRef}>
        <div
          className={`w-full bg-[#2B2C2F] text-gray-400 rounded p-1 pr-6 cursor-pointer truncate ${className || ''
            }`}
          onClick={() => setIsOpen((prev) => !prev)}
        >
          {selected?.label?.length > 20
            ? `${selected.label.slice(0, 17)}...`
            : selected?.label || 'Select'}
        </div>
        {isOpen && (
          <ul
            className={`absolute z-[50] w-full ${openUp ? 'bottom-full mb-1' : 'top-full mt-1'
              } bg-[#2B2C2F] border border-[#444] rounded shadow
           text-gray-300 max-h-[80px] overflow-y-auto ${menuClassName} flex flex-col gap-1`}
          >
            {options.map((opt) => (
              <li
                key={opt.value}
                className={`px-2 py-1 text-[10px] cursor-pointer
              hover:bg-[#3F3F3F] 
               ${opt.value === value ? 'bg-[#3F3F3F] text-white' : ''}
              ${opt.dis ? 'opacity-40 cursor-not-allowed' : ''}
              `}
                onClick={() => {
                  if (!opt.dis) {
                    onChange({ target: { value: opt.value } });
                    setIsOpen(false);
                  }
                }}
              >
                {opt.label}
              </li>
            ))}
          </ul>
        )}
        {iconsPath?.vectorImage && (
          <div className="absolute inset-y-0 right-1 flex items-center pointer-events-none">
            <img src={iconsPath.vectorImage} alt="dropdown icon" />
          </div>
        )}
      </div>
    </div>
  );
};

export const GridDropInput = ({
  label,
  value,
  onChange,
  options,
  isFont = false,
}) => {
  const [query, setQuery] = useState('');
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [displayLimit, setDisplayLimit] = useState(50);
  const loadedFonts = useRef(new Set());
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const isTypingRef = useRef(false); // false = just opened, show all; true = user is searching

  useEffect(() => {
    // Show ALL options when dropdown is first opened (not yet typing).
    // Filter only once the user starts typing.
    const filtered = isTypingRef.current
      ? options.filter(
          (opt) =>
            opt.label.toLowerCase().includes(query.toLowerCase()) ||
            opt.value.toLowerCase().includes(query.toLowerCase())
        )
      : options;
    setFilteredOptions(filtered.slice(0, displayLimit));

    // Highlight the currently selected option
    const selectedIndex = filtered.findIndex((opt) => opt.value === value);
    setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : 0);
  }, [query, options, displayLimit, value]);

  const handleSelect = (val) => {
    if (val !== undefined && val !== null) {
      isTypingRef.current = false;
      setQuery(val);
      onChange({ target: { value: val } });
    }
    setShowDropdown(false);
  };

  useEffect(() => {
    // Sync display: show the selected value (not the full label) in the input box
    if (value !== undefined && value !== null) setQuery(value);
  }, [value]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyDown = (e) => {
    if (!showDropdown) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev < filteredOptions.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev > 0 ? prev - 1 : filteredOptions.length - 1
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredOptions[highlightedIndex]) {
        handleSelect(filteredOptions[highlightedIndex].value);
      }
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  const handleScroll = (e) => {
    const bottom =
      e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 10;
    if (bottom) {
      setDisplayLimit((prev) => prev + 30);
    }
  };

  useEffect(() => {
    if (isFont && showDropdown) {
      const batch = filteredOptions
        .slice(0, displayLimit)
        .map((opt) => opt.label)
        .filter((label) => !loadedFonts.current.has(label));

      if (batch.length > 0) {
        batch.forEach((font) => loadedFonts.current.add(font));
        loadGoogleFontsBatch(batch);
      }
    }
  }, [filteredOptions, displayLimit, showDropdown, isFont]);

  useEffect(() => {
    if (showDropdown && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      const container =
        inputRef.current.closest('.scrollbar') ||
        inputRef.current.closest('.overflow-auto') ||
        inputRef.current.closest('.control-panel');

      const bottomBoundary = container
        ? container.getBoundingClientRect().bottom
        : window.innerHeight;

      const spaceBelow = bottomBoundary - rect.bottom;
      const dropdownHeight = 180;
      if (spaceBelow < dropdownHeight && rect.top > dropdownHeight) {
        setOpenUp(true);
      } else {
        setOpenUp(false);
      }
    }
  }, [showDropdown]);

  // Scroll the selected/highlighted item into view when the dropdown opens
  useEffect(() => {
    if (showDropdown && listRef.current && highlightedIndex >= 0) {
      const items = listRef.current.querySelectorAll('li');
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({ block: 'nearest' });
      }
    }
  }, [showDropdown, highlightedIndex]);

  return (
    <div className="mb-2 flex items-center gap-2">
      {label && <LabelHover label={label} />}
      <div className="relative flex-[16]" ref={inputRef}>
        <input
          type="text"
          value={query}
          onChange={(e) => {
            isTypingRef.current = true;
            setQuery(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={() => {
            // Show actual selected text in the box, and all options in the dropdown
            isTypingRef.current = false;
            setQuery(value || '');
            setShowDropdown(true);
          }}
          placeholder={value || 'Search font...'}
          onKeyDown={handleKeyDown}
          className="w-full bg-[#2B2C2F] text-gray-400 rounded p-1 pr-8 text-[10px] truncate focus:outline-none focus:border-none"
        />
        {showDropdown && (
          <ul
            ref={listRef}
            onScroll={handleScroll}
            className={`absolute z-10 w-full ${
              openUp ? 'bottom-full mb-1' : 'top-full mt-1'
            } max-h-40 overflow-y-auto bg-[#2B2C2F] text-gray-200 border border-gray-600 rounded text-[10px] homepage-scrollbar flex flex-col gap-1`}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt, index) => (
                <li
                  style={{ fontFamily: isFont ? opt.value : '' }} // Fixed: use value instead of label
                  key={opt.value}
                  onClick={() => handleSelect(opt.value)}
                  className={`px-2 py-1 cursor-pointer text-[11px] ${
                    opt?.dis ? 'text-gray-500 pointer-events-none' : ''
                  } ${
                    index === highlightedIndex
                      ? 'bg-[#444447] text-white'
                      : 'hover:bg-[#444447] hover:text-white'
                  }`}
                >
                  {opt.label}
                </li>
              ))
            ) : (
              <li className="px-2 py-1 text-gray-400">No match found</li>
            )}
          </ul>
        )}
        {iconsPath?.vectorImage && (
          <button
            type="button"
            className="absolute inset-y-0 right-1 flex items-center"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              if (showDropdown) {
                setShowDropdown(false);
                return;
              }
              isTypingRef.current = false;
              setQuery(value || '');
              setShowDropdown(true);
              inputRef.current?.querySelector('input')?.focus();
            }}
          >
            <img
              src={iconsPath.vectorImage}
              alt="dropdown icon"
              className={`${showDropdown ? 'rotate-180' : 'rotate-0'} transition-transform`}
            />
          </button>
        )}
      </div>
    </div>
  );
};

export const DividerRight = React.memo(() => {
  return (
    <img
      className="right-divider my-[2px]"
      src={iconsPath?.line3Image}
      alt=""
    />
  );
});

const ShadowCheckbox = ({ label, checked, onToggle }) => (
  <button
    type="button"
    className="flex items-center gap-1.5 text-[10px] text-gray-400 hover:text-gray-200"
    onClick={onToggle}
  >
    <span
      className="w-4 h-4 bg-[#2B2C2F] border border-gray-700 flex items-center justify-center rounded shrink-0"
      aria-hidden
    >
      {checked && (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-3 w-3 text-white"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
            clipRule="evenodd"
          />
        </svg>
      )}
    </span>
    <span>{label}</span>
  </button>
);

/** Shadow row: cast + receive checkboxes (material panel). */
export const MaterialShadowRow = React.memo(
  ({ castChecked, receiveChecked, onCastToggle, onReceiveToggle }) => (
    <div className="mb-2 flex items-center gap-[3px] w-full">
      <LabelHover label="Shadow" />
      <div className="flex flex-[16] items-center gap-4 px-3">
        <ShadowCheckbox
          label="cast"
          checked={!!castChecked}
          onToggle={onCastToggle}
        />
        <ShadowCheckbox
          label="receive"
          checked={!!receiveChecked}
          onToggle={onReceiveToggle}
        />
      </div>
    </div>
  )
);

export const ToggleCheckbox = ({
  label,
  checked,
  onToggle,
  material = false,
  className = '',
}) => {
  return (
    <div className={`mb-2 flex items-center gap-[3px] w-full ${className}`}>
      {label && <LabelHover label={label} />}

      <div className="flex-[16] px-3">
        <div
          className="w-4 h-4 bg-[#2B2C2F] border border-gray-700 flex items-center justify-center cursor-pointer rounded"
          onClick={onToggle}
        >
          {checked && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-3 w-3 text-white"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          )}
        </div>
      </div>
    </div>
  );
};

export const ColorInput = ({
  label = 'Color',
  color = '#FFFFFF',
  setColor,
  onLiveChange,
  /** When true, Redux/parent updates only on picker release — drag stays smooth. */
  commitOnComplete = false,
  icon = addcomponent.icon1,
  className = '',
  bottomPanelSelector = null, // e.g. '.timeline-panel' or '[data-panel="timeline"]'
}) => {
  const [colorValue, setColorValue] = useState(() => {
    const defaultColor = '#FFFFFF';
    const colorToUse = color || defaultColor;
    try {
      const r = parseInt(colorToUse.slice(1, 3), 16);
      const g = parseInt(colorToUse.slice(3, 5), 16);
      const b = parseInt(colorToUse.slice(5, 7), 16);
      return { r, g, b, a: 1 };
    } catch (error) {
      console.warn('Invalid color format, using default:', error);
      return { r: 255, g: 255, b: 255, a: 1 };
    }
  });

  const [hexInput, setHexInput] = useState(color || '#FFFFFF');
  const [isValidHex, setIsValidHex] = useState(true);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);

  // Sync when color prop changes externally
  useEffect(() => {
    if (color && typeof color === 'string') {
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);
      setColorValue({ r, g, b, a: 1 });
      setHexInput(color);
      setIsValidHex(true);
    }
  }, [color]);

  const getBottomBoundary = () => {
    if (bottomPanelSelector) {
      const el = document.querySelector(bottomPanelSelector);
      if (el) return el.getBoundingClientRect().top;
    }
    return window.innerHeight;
  };

  const calculatePickerPosition = () => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const pickerWidth = 250;
    const pickerHeight = 500; // SketchPicker with swatches is taller than 400

    // Use bottom panel top as the lower boundary instead of full vh
    const bottomBoundary = getBottomBoundary();

    let left = rect.left - pickerWidth - 145;
    let top;

    const spaceBelow = bottomBoundary - rect.bottom;
    const spaceAbove = rect.top;

    if (spaceAbove >= pickerHeight + 20) {
      // Open upward: bottom of picker aligns to top of trigger
      const bottomPanel = document.querySelector('#timeline-panel');
      const isTimelineOpen =
        bottomPanel && bottomPanel.getBoundingClientRect().height > 0;
      const bottomBoundary = isTimelineOpen
        ? bottomPanel.getBoundingClientRect().top
        : window.innerHeight;

      top = Math.min(rect.top, bottomBoundary - pickerHeight - 20);
      top = Math.max(20, top);
    } else if (spaceBelow >= pickerHeight + 20) {
      // Open downward: top of picker aligns to bottom of trigger
      top = rect.bottom + 8;
    } else {
      // Neither — clamp as high as possible above the bottom panel
      top = Math.max(20, bottomBoundary - pickerHeight - 20);
    }

    // Horizontal clamping
    if (left < 20) left = 20;
    if (left + pickerWidth > vw - 20) left = rect.right + 8;

    setPickerPosition({ top, left });
  };

  const toggleColorPicker = () => {
    if (!showColorPicker) calculatePickerPosition();
    setShowColorPicker((prev) => !prev);
  };

  // Recalculate on resize
  useEffect(() => {
    const handleResize = () => {
      if (showColorPicker) calculatePickerPosition();
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [showColorPicker]);

  const rgbToHex = ({ r, g, b }) => {
    const toHex = (v) => Math.round(v).toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
  };

  const handleHexInputChange = (e) => {
    const hex = e.target.value;
    setHexInput(hex);
    const hexRegex = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;
    if (hexRegex.test(hex)) {
      const clean = hex.replace('#', '');
      const r = parseInt(
        clean.length === 3 ? clean[0].repeat(2) : clean.slice(0, 2),
        16
      );
      const g = parseInt(
        clean.length === 3 ? clean[1].repeat(2) : clean.slice(2, 4),
        16
      );
      const b = parseInt(
        clean.length === 3 ? clean[2].repeat(2) : clean.slice(4, 6),
        16
      );
      setColorValue({ r, g, b, a: colorValue.a });
      setColor(hex);
      setIsValidHex(true);
    } else {
      setIsValidHex(false);
    }
  };

  const handleColorChange = (color) => {
    setColorValue(color.rgb);
    const hex = rgbToHex(color.rgb);
    setHexInput(hex);
    setIsValidHex(true);
    if (typeof onLiveChange === 'function') {
      onLiveChange(hex);
    }
    // Live parent commit unless deferred to release
    if (!commitOnComplete && typeof setColor === 'function') {
      setColor(hex);
    }
  };

  const handleColorChangeComplete = (color) => {
    setColorValue(color.rgb);
    const hex = rgbToHex(color.rgb);
    setHexInput(hex);
    setIsValidHex(true);
    if (typeof setColor === 'function') {
      setColor(hex);
    }
  };

  const handleEyeDropper = async () => {
    try {
      const eyeDropper = new EyeDropper();
      const result = await eyeDropper.open();
      const hex = result.sRGBHex.toUpperCase();
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      setColorValue({ r, g, b, a: 1 });
      setHexInput(hex);
      setColor(hex);
      setIsValidHex(true);
    } catch { }
  };

  const getContrastTextColor = (r, g, b) => {
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
  };

  return (
    <>
      <div
        className={`mb-2 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 ${className}`}
      >
        {label && <LabelHover label={label} />}
        <div className="relative min-w-0 flex-[16]">
          <div className="flex items-center gap-1">
            <input
              ref={triggerRef}
              type="text"
              value={hexInput}
              onChange={handleHexInputChange}
              className={`flex-grow w-full bg-[#2B2C2F] border ${isValidHex ? 'border-gray-700' : 'border-red-500'
                } text-[10px] rounded cursor-pointer p-1 focus:outline-none color-trigger`}
              placeholder="#FFFFFF"
              style={{
                backgroundColor: `rgba(${colorValue.r}, ${colorValue.g}, ${colorValue.b}, ${colorValue.a})`,
                color: getContrastTextColor(
                  colorValue.r,
                  colorValue.g,
                  colorValue.b
                ),
              }}
              onClick={toggleColorPicker}
            />
            <button
              type="button"
              className="p-1 color-trigger flex-shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                handleEyeDropper();
              }}
            >
              <img
                src={icon}
                className="h-3 w-3 sm:h-4 sm:w-4 text-gray-300"
                alt="Eye Dropper"
              />
            </button>
          </div>
        </div>
      </div>

      <ColorPickerModal
        isOpen={showColorPicker}
        onClose={() => setShowColorPicker(false)}
        position={pickerPosition}
      >
        <SketchPicker
          color={colorValue}
          onChange={handleColorChange}
          onChangeComplete={handleColorChangeComplete}
          disableAlpha={true}
          styles={{
            default: {
              picker: {
                background: '#1a1a1a',
                padding: '10px',
                borderRadius: '4px',
                boxShadow: 'none',
                width: '100%',
                maxWidth: '225px',
                minWidth: '200px',
              },
              saturation: { borderRadius: '4px' },
              hue: { borderRadius: '4px' },
              alpha: { borderRadius: '4px' },
              input: {
                background: '#2B2C2F',
                color: '#fff',
                border: '1px solid #333',
                borderRadius: '4px',
                padding: '4px',
                fontSize: '12px',
              },
            },
          }}
        />
      </ColorPickerModal>
    </>
  );
};

export const GridTextInput = React.memo(
  ({
    label = '',
    value,
    onChange,
    flag = false,
    className = '',
    readOnly = false,
    placeholder,
    multiline = false,
    rows = 1,
    endAdornment = null,
    startAdornment = () => { },
    startAdornment1 = null,
    type,
    maxDigits,
    maxValue,
    onMaxReached,
    minValue,
    onMinReached,
    step,
    debounceMs = 300,
  }) => {
    const isFocused = useRef(false);
    const textareaRef = useRef(null);
    const [localValue, setLocalValue] = useState(value);
    const lastSyncedRef = useRef(value);
    const syncRanRef = useRef(false);
    const setLocalValueRef = useRef(setLocalValue);
    setLocalValueRef.current = setLocalValue;

    const autoResizeTextarea = (element) => {
      const el = element || textareaRef.current;
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    };

    useEffect(() => {
      if (multiline) {
        autoResizeTextarea();
      }
    }, [localValue, multiline]);

    useEffect(() => {
      if (!syncRanRef.current) {
        syncRanRef.current = true;
        lastSyncedRef.current = value;
        return;
      }
      const prev = lastSyncedRef.current;
      const same =
        Object.is(prev, value) ||
        (typeof prev === 'number' &&
          typeof value === 'number' &&
          Number.isFinite(prev) &&
          Number.isFinite(value) &&
          Math.abs(prev - value) < 1e-10);
      if (same) return;
      if (isFocused.current) return;
      lastSyncedRef.current = value;
      setLocalValue(value);
    }, [value]);

    useEffect(() => {
      const setter = (v) => setLocalValueRef.current(v);
      if (typeof endAdornment === 'function') endAdornment(setter);
      if (typeof startAdornment === 'function') startAdornment(setter);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      const delay =
        typeof debounceMs === 'number' && debounceMs >= 0 ? debounceMs : 300;
      const handler = setTimeout(() => {
        const eq =
          localValue === value ||
          (typeof localValue === 'number' &&
            typeof value === 'number' &&
            Number.isFinite(localValue) &&
            Number.isFinite(value) &&
            Math.abs(localValue - value) < 1e-10);
        if (!eq) {
          let finalVal = localValue;
          if (localValue !== '' && localValue !== '-') {
            const num = Number(localValue);
            if (maxValue !== undefined && num > maxValue) finalVal = maxValue;
            else if (minValue !== undefined && num < minValue)
              finalVal = minValue;
          }
          onChange({ target: { value: finalVal } });
        }
      }, delay);
      return () => clearTimeout(handler);
    }, [localValue, value, debounceMs, maxValue, minValue]);

    // Determine input type
    const inputType =
      type ||
      (typeof value === 'number' ||
        (typeof value === 'string' && /^-?\d*(\.\d*)?$/.test(value))
        ? 'number'
        : 'text');

    return (
      <div className={`mb-3 flex items-center gap-2 ${className}`}>
        {startAdornment(label)}
        {label && !flag && <LabelHover label={label} />}
        <div className="flex-[16] relative flex items-center">
          {multiline ? (
            <textarea
              ref={textareaRef}
              value={localValue}
              onChange={(e) => {
                setLocalValue(e.target.value);
                autoResizeTextarea(e.target);
              }}
              readOnly={readOnly}
              onFocus={() => {
                isFocused.current = true;
              }}
              onBlur={() => {
                isFocused.current = false;
              }}
              placeholder={placeholder}
              rows={rows}
              className="w-full bg-[#2B2C2F] rounded p-1 text-[10px] text-gray-400 focus:outline-none overflow-hidden resize-none"
            />
          ) : (
            <input
              type={inputType}
              value={localValue}
              max={maxValue}
              min={minValue}
              step={step}
              onChange={(e) => {
                let val = e.target.value;

                if (inputType === 'number') {
                  // ✅ Block negative sign when min is 0 or positive
                  const allowNegative = minValue === undefined || minValue < 0;
                  const pattern = allowNegative
                    ? /^[-]?\d*(\.\d*)?$/
                    : /^\d*(\.\d*)?$/;

                  if (!pattern.test(val)) return;

                  // ✅ Allow clearing the input
                  if (val === '' || (allowNegative && val === '-')) {
                    setLocalValue(val);
                    return;
                  }

                  const num = Number(val);

                  // Max limit
                  if (maxValue !== undefined && num > maxValue) {
                    onMaxReached?.(maxValue);
                    return;
                  }

                  // Min limit
                  if (
                    minValue !== undefined &&
                    num < minValue &&
                    val !== '' &&
                    val !== '-'
                  ) {
                    onMinReached?.(minValue);
                    return;
                  }
                }

                // maxDigits logic
                if (maxDigits && inputType === 'number') {
                  const digitsOnly = val.replace(/\D/g, '');
                  if (digitsOnly.length > maxDigits) return;
                }

                setLocalValue(val);
              }}
              onFocus={() => {
                isFocused.current = true;
              }}
              onBlur={(e) => {
                isFocused.current = false;
                // ✅ Clamp value on blur in case arrow keys bypassed onChange
                if (inputType === 'number') {
                  let num = Number(e.target.value);
                  if (maxValue !== undefined && num > maxValue) {
                    setLocalValue(maxValue);
                    onMaxReached?.(maxValue);
                  } else if (minValue !== undefined && num < minValue) {
                    setLocalValue(minValue);
                    onMinReached?.(minValue);
                  }
                }
              }}
              readOnly={readOnly}
              placeholder={placeholder}
              className="w-full bg-[#2B2C2F] rounded p-1 text-[10px] text-white focus:outline-none"
            />
          )}
        </div>
      </div>
    );
  }
);
export const GridInputWithIcon = ({
  label = '',
  defaultValue = '',
  icon = addcomponent.group306,
  onClick,
  className = '',
  readOnly,
  value,
  onChange,
}) => {
  return (
    <div className={`mb-2 ${className} flex items-center gap-2`}>
      {label && <LabelHover label={label} />}
      <div className="flex-[16] flex items-center ">
        <input
          type="text"
          value={value}
          onChange={onChange}
          className="w-full bg-[#2B2C2F] rounded p-1 text-[10px] text-gray-400 focus:outline-none"
          readOnly={readOnly}
          disabled={readOnly}
        />
        <button className="ml-2 p-1" onClick={onClick}>
          <img src={icon} className="h-4 w-4" alt="icon" />
        </button>
      </div>
    </div>
  );
};

export const ToggleDropdown = React.memo(
  ({ label, toggle, setToggle, children }) => {
    return (
      <div>
        <div
          className="right-section-header py-2"
          onClick={() => setToggle(!toggle)}
        >
          <span className="text-[12px]  ">{label}</span>
          <span>
            <img
              src={iconsPath?.vectorImage}
              className={`${!toggle ? '-rotate-90 ' : 'rotate-0'} transition `}
            />
          </span>
        </div>
        {toggle && children}
      </div>
    );
  }
);

export const MultiSelectionIcon = React.memo(
  ({ label, valueArr, onClick, className, selected }) => {
    return (
      <div className={`mb-2 flex items-center  ${className}`}>
        {label && <LabelHover label={label} />}
        <div className="flex-[16] flex justify-between items-center">
          {valueArr.map((val, index) => (
            <div
              key={index}
              onClick={() => onClick(val.value)}
              className={`flex items-center space-x-2  border-[1px] ${selected == val?.value ? 'bg-[#2b2c2f]' : 'bg-black'} border-[#2B2C2F] px-3 py-1 rounded-[10px]  cursor-pointer`}
            >
              <img
                src={iconsPath[val.icon]}
                alt={val.label}
                className="h-[1rem] w-[1rem] cursor-pointer "
              />
            </div>
          ))}
        </div>
      </div>
    );
  }
);

export const GridTwoInput = React.memo(
  ({
    label,
    value1,
    value2,
    onChange1,
    onChange2,
    onBlur1,
    onBlur2,
    placeholder1 = '',
    placeholder2 = '',
    type = 'number',
    step = 'any',
    min1,
    max1,
    min2,
    max2,
    onKeyDown1,
    onKeyDown2,
  }) => {
    return (
      <div className="mb-2 flex items-center">
        <label className="text-gray-400 text-[10px] min-w-[80px] mb-1 mr-2">
          {label}
        </label>
        <div className="flex items-center flex-1">
          <input
            type={type}
            step={step}
            min={min1}
            max={max1}
            value={value1}
            onChange={onChange1}
            onBlur={onBlur1}
            onKeyDown={onKeyDown1}
            placeholder={placeholder1}
            className="w-full bg-[#2B2C2F] rounded p-1 text-[10px] text-gray-400 focus:outline-none"
          />
          <span className="mx-2 text-gray-400">-</span>
          <input
            type={type}
            step={step}
            min={min2}
            max={max2}
            value={value2}
            onChange={onChange2}
            onBlur={onBlur2}
            onKeyDown={onKeyDown2}
            placeholder={placeholder2}
            className="w-full bg-[#2B2C2F] rounded p-1 text-[10px] text-gray-400 focus:outline-none"
          />
        </div>
      </div>
    );
  }
);

export const PublishSpinner = () => (
  <svg
    className="animate-spin h-4 w-4 text-white"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    ></circle>
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    ></path>
  </svg>
);

export const CameraZoom = React.memo(({ objectt }) => {
  const { scene } = objectt;
  const [zoomLevel, setZoomLevel] = useState(100);
  const [cameraRef, setCameraRef] = useState(null);
  const zoomLevelRef = useRef(100);

  // Keep ref in sync with state
  useEffect(() => {
    zoomLevelRef.current = zoomLevel;
  }, [zoomLevel]);

  // Track camera reference
  useEffect(() => {
    if (!scene) return;

    const updateCameraRef = () => {
      const camera =
        window.currentCamera ||
        window.camera ||
        (window.controls && window.controls.object);
      if (camera) {
        setCameraRef(camera);
        if (camera.isPerspectiveCamera) {
          const center = new Three.Vector3(0, 0, 0);
          const initialDistance = camera.position.distanceTo(center);
          if (!scene.userData.initialZoomDistance) {
            scene.userData.initialZoomDistance = initialDistance;
          }
        }
        if (camera.isOrthographicCamera) {
          if (!scene.userData.initialOrthoZoom) {
            scene.userData.initialOrthoZoom = camera.zoom;
          }
        }
      }
    };

    updateCameraRef();
    const interval = setInterval(updateCameraRef, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [scene]);

  // Update zoom level display
  useEffect(() => {
    if (!cameraRef || !scene) return;

    const updateZoomLevel = () => {
      if (cameraRef.isPerspectiveCamera) {
        const center = new Three.Vector3(0, 0, 0);
        const currentDistance = cameraRef.position.distanceTo(center);
        const initialDistance =
          scene.userData.initialZoomDistance || currentDistance;
        if (!scene.userData.initialZoomDistance) {
          scene.userData.initialZoomDistance = currentDistance;
        }
        const newZoomLevel = Math.round(
          (initialDistance / currentDistance) * 100
        );
        const clamped = Math.min(Math.max(newZoomLevel, 10), 200);
        setZoomLevel(clamped);
        zoomLevelRef.current = clamped;
      } else if (cameraRef.isOrthographicCamera) {
        const initialZoom =
          scene.userData.initialOrthoZoom || cameraRef.zoom || 1;
        if (!scene.userData.initialOrthoZoom) {
          scene.userData.initialOrthoZoom = cameraRef.zoom || 1;
        }
        const percent = Math.round((cameraRef.zoom / initialZoom) * 100);
        const clamped = Math.min(Math.max(percent, 10), 200);
        setZoomLevel(clamped);
        zoomLevelRef.current = clamped;
      }
    };

    if (window.controls) {
      window.controls.addEventListener('change', updateZoomLevel);
    }
    const interval = setInterval(updateZoomLevel, 100);

    return () => {
      if (window.controls) {
        window.controls.removeEventListener('change', updateZoomLevel);
      }
      clearInterval(interval);
    };
  }, [cameraRef, scene]);

  // Zoom in handler
  const zoomIn = () => {
    if (!cameraRef || !scene) return;

    if (cameraRef.isPerspectiveCamera) {
      const center = new Three.Vector3(0, 0, 0);
      const initialDistance = scene.userData.initialZoomDistance;
      if (!initialDistance) return;
      const newZoom = Math.min(zoomLevelRef.current + 10, 200);
      zoomLevelRef.current = newZoom;
      setZoomLevel(newZoom);
      const factor = initialDistance / (newZoom / 100);
      const dir = cameraRef.position.clone().sub(center).normalize();
      cameraRef.position.copy(dir.multiplyScalar(factor).add(center));
      cameraRef.updateProjectionMatrix?.();
      window.controls?.update?.();
      window.renderer?.render?.(scene, cameraRef);
    } else if (cameraRef.isOrthographicCamera) {
      const initialZoom =
        scene.userData.initialOrthoZoom || cameraRef.zoom || 1;
      const maxZoom = initialZoom * 2;
      let newZoom = cameraRef.zoom + initialZoom / 10;
      if (newZoom > maxZoom) newZoom = maxZoom;
      cameraRef.zoom = newZoom;
      cameraRef.updateProjectionMatrix();
      window.controls?.update?.();
      window.renderer?.render?.(scene, cameraRef);
    }
  };

  // Zoom out handler
  const zoomOut = () => {
    if (!cameraRef || !scene) return;

    if (cameraRef.isPerspectiveCamera) {
      const center = new Three.Vector3(0, 0, 0);
      const initialDistance = scene.userData.initialZoomDistance;
      if (!initialDistance) return;
      const newZoom = Math.max(zoomLevelRef.current - 10, 10);
      zoomLevelRef.current = newZoom;
      setZoomLevel(newZoom);
      const factor = initialDistance / (newZoom / 100);
      const dir = cameraRef.position.clone().sub(center).normalize();
      cameraRef.position.copy(dir.multiplyScalar(factor).add(center));
      cameraRef.updateProjectionMatrix?.();
      window.controls?.update?.();
      window.renderer?.render?.(scene, cameraRef);
    } else if (cameraRef.isOrthographicCamera) {
      const initialZoom =
        scene.userData.initialOrthoZoom || cameraRef.zoom || 1;
      const minZoom = initialZoom / 10;
      let newZoom = cameraRef.zoom - initialZoom / 10;
      if (newZoom < minZoom) newZoom = minZoom;
      cameraRef.zoom = newZoom;
      cameraRef.updateProjectionMatrix();
      window.controls?.update?.();
      window.renderer?.render?.(scene, cameraRef);
    }
  };

  return (
    <span className="zoom-control">
      <img
        className="zoom-control-img"
        src={iconsPath?.vector5Image}
        alt="Zoom Out"
        onClick={zoomOut}
        style={{ cursor: 'pointer' }}
      />
      <p className="w-[35px] text-center">{zoomLevel}%</p>
      <img
        className="zoom-control-img"
        src={iconsPath?.group20Image}
        alt="Zoom In"
        onClick={zoomIn}
        style={{ cursor: 'pointer' }}
      />
    </span>
  );
});

export const PreviewLeftComponent = React.memo(({ objectt }) => {
  const { window_size, preview_type } = useSelector((state) => state.window);
  const { openModals } = useSelector((state) => state.assets_object);
  const { handleReplay } = usePreviewResize({
    completeObject: objectt,
  });
  const { projectId } = objectt;
  // useEffect(() => {
  //   handleReplay();
  // }, []);

  useEffect(() => {
    store.dispatch(update_preview_type('default'));
  }, []);

  const handleUpdatePreview = (e) => {
    const value = e.target.value;
    var data = { ...window_size };
    if (value === 'default') {
      data.height = window.innerHeight;
      data.width = window.innerWidth;
    } else if (value === 'mobile') {
      data.width = 375; // iPhone 12 typical width
      data.height = 812; // iPhone 12 typical height
    } else if (value === 'tab') {
      data.width = 600; // iPad typical width
      data.height = window.innerHeight < 960 ? window.innerHeight : 960; // iPad typical height
    }
    store.dispatch(update_preview_type(e.target.value));
    updatePreviewDimention(data);
    store.dispatch(update_full_window_size(data));
  };

  const url = `${process.env.REACT_APP_SPATIAL_WEB_URL}preview-page/${projectId}`;
  // const url = `https://devgrid.spatialgrid.ai/preview-page/${projectId}`;

  const handleCopyLink = async () => {
    try {
      const url = `${process.env.REACT_APP_SPATIAL_WEB_URL}preview-page/${projectId}`;
      await navigator.clipboard.writeText(url);
      toast.success('Link copied successfully');
      store.dispatch(
        updateOpenModals({
          name: 'preview_share',
          isOpen: false,
        })
      );
    } catch (err) {
      toast.error('Something went wrong while copying the link');
      console.error(err);
    }
  };
  const handleShareViaEmail = () => {
    // const url = `${process.env.REACT_APP_SPATIAL_WEB_URL}preview-page/${projectId}`;
    const subject = encodeURIComponent('Check out this 3D Scene');
    const body = encodeURIComponent(
      `Hi,\n\nTake a look at this scene:\n${url}`
    );

    // Open default email client
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleGetEmbedCode = async () => {
    // const url = `${process.env.REACT_APP_SPATIAL_WEB_URL}preview-page/${projectId}`;

    const embedCode = `<iframe src="${url}" width="100%" height="500" frameborder="0" allowfullscreen></iframe>`;

    navigator.clipboard
      .writeText(embedCode)
      .then(() => toast.success('Embed code copied to clipboard!'))
      .catch(() => toast.error('Failed to copy embed code'));
  };

  const shareOptions = [
    { label: 'Copy Link', icon: iconsPath.copy_link, onClick: handleCopyLink },
    {
      label: 'Share via email',
      icon: iconsPath.share_vie_email,
      onClick: handleShareViaEmail,
    },
    // {
    //   label: 'Get embed code',
    //   icon: iconsPath.embaded_code,
    //   onClick: handleGetEmbedCode,
    // },
  ];

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex justify-center items-center w-full gap-2">
        <div
          onClick={handleReplay}
          className="cursor-pointer flex flex-1 p-[5px] gap-2 justify-center items-center bg-[#2D2E32] rounded-[5px]"
        >
          <img className="h-[11px] w-[11px]" src={iconsPath.replayIcon} />
          <div className="text-[#FFFFFF] text-[10px]">Replay</div>
        </div>
        <div className="cursor-pointer flex flex-1 p-[5px] gap-2 justify-center items-center bg-[#2D2E32] rounded-[5px]">
          <img className="h-[11px] w-[11px]" src={iconsPath.uploadIcon2} />
          <div className="text-[#FFFFFF] text-[10px]">Export</div>
        </div>
        <div
          onClick={() =>
            store.dispatch(
              updateOpenModals({
                name: 'preview_share',
                isOpen: true,
              })
            )
          }
          className="cursor-pointer flex flex-1 p-[5px] gap-2 justify-center items-center bg-[#2D2E32] rounded-[5px]"
        >
          <img className="h-[11px] w-[11px]" src={iconsPath?.PreviewShare} />
          <div className="text-[#FFFFFF] text-[10px]">Share</div>
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <label className="flex-1 block text-[#fff] text-[13px] mb-1 min-w-[50px] whitespace-nowrap">
          Preview Experience On
        </label>
        <div className="flex flex-col gap-1 text-[#ffffff]">
          <GridDropdown
            value={preview_type || 'default'}
            onChange={handleUpdatePreview}
            options={[
              { label: 'Default Preview', value: 'default' },
              { label: 'Mobile', value: 'mobile' },
              { label: 'Tab', value: 'tab' },
            ]}
            className="h-7 flex items-center text-[14px]  "
          />
        </div>
      </div>
      <div className="flex justify-center items-center w-full gap-2 relative bottom-2">
        <div className="cursor-pointer flex flex-1 p-[5px] px-2 gap-2  justify-start items-center bg-[#2D2E32] rounded-[5px]">
          <div className="flex-[2] text-[#FF6464] text-[12px]">W</div>
          <input
            type="number"
            value={window_size.width}
            onChange={(e) =>
              store.dispatch(
                update_window_size_by_key({
                  value: e.target.value,
                  key: 'width',
                })
              )
            }
            placeholder="--"
            onBlur={() => {
              const data = { ...window_size }; // ✅ Create a new object

              if (window_size.width > window.innerWidth) {
                data.width = window.innerWidth;
              } else if (window_size?.width < 300) {
                data.width = 300;
              }
              updatePreviewDimention(data);
              store.dispatch(update_full_window_size(data)); // ✅ Now this will trigger re-render
            }}
            className="flex-[2] text-[12px] text-white bg-transparent outline-none border-none w-full"
          />
        </div>

        <div className="cursor-pointer flex flex-1 p-[5px] px-2 gap-2 justify-start items-center bg-[#2D2E32] rounded-[5px]">
          <div className="flex-[2] text-[#FF6464] text-[12px]">H</div>
          <input
            type="number"
            onChange={(e) =>
              store.dispatch(
                update_window_size_by_key({
                  value: e.target.value,
                  key: 'height',
                })
              )
            }
            placeholder="--"
            value={window_size.height}
            onBlur={() => {
              var data = { ...window_size };
              if (window_size?.height > window.innerHeight) {
                data.height = window.innerHeight;
              } else if (window_size?.height < 300) {
                data.height = 300;
              }
              updatePreviewDimention(data);
              store.dispatch(update_full_window_size(data));
            }}
            className="flex-[2] text-[12px] text-white bg-transparent outline-none border-none w-full"
          />
        </div>
      </div>
      <SimpleModal
        isOpen={openModals.preview_share}
        onClose={() => {
          store.dispatch(
            updateOpenModals({
              name: 'preview_share',
              isOpen: false,
            })
          );
        }}
        showClose
        className={
          'bg-[#131418]  shadow-lg rounded-[10px] px-6 py-6 !max-w-[340px] relative'
        }
      >
        <div className="text-[20px]">Share</div>
        <div className="flex mt-3 flex-col gap-5">
          {shareOptions.map((item, index) => (
            <ListForShare
              key={index}
              label={item.label}
              icon={item.icon}
              onClick={item?.onClick}
            />
          ))}
        </div>
      </SimpleModal>
    </div>
  );
});

/** Material texture row: label, visibility checkbox, thumbnail, download. */
export const MaterialMapRow = React.memo(
  ({ label, previewUrl, texture, checked, disabled, onToggle, onThumbnailClick }) => {
    const canDownload = !!(texture || previewUrl);

    return (
      <div className="mb-2 flex items-center gap-[3px] w-full">
        <LabelHover label={label} />
        <div className="grid flex-[16] grid-cols-[16px_40px_20px] items-center gap-2 px-3">
          <div
            className={`w-4 h-4 bg-[#2B2C2F] border border-gray-700 flex items-center justify-center rounded shrink-0 ${disabled
                ? 'opacity-40 cursor-not-allowed'
                : 'cursor-pointer hover:border-gray-500'
              }`}
            onClick={() => {
              if (!disabled && onToggle) onToggle();
            }}
          >
            {checked && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-3 w-3 text-[#b794f4]"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </div>
          <button
            type="button"
            className="w-[40px] h-[20px] rounded bg-[#1a1a1a] border border-gray-700 overflow-hidden flex items-center justify-center shrink-0 hover:border-gray-500"
            onClick={onThumbnailClick}
            title={previewUrl ? 'Replace texture' : 'Upload texture'}
          >
            {previewUrl ? (
              <img
                key={previewUrl}
                src={previewUrl}
                alt=""
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-[9px] text-gray-600">—</span>
            )}
          </button>
          {canDownload ? (
            <button
              type="button"
              className="w-5 h-5 rounded flex items-center justify-center text-gray-400 hover:text-white hover:bg-[#2B2C2F] transition-colors"
              onClick={() =>
                downloadFullQualityMaterialMap(texture, label, previewUrl)
              }
              title={`Download ${label} (full quality)`}
              aria-label={`Download ${label}`}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-3.5 h-3.5"
                aria-hidden="true"
              >
                <path d="M12 3v12" />
                <path d="m7 10 5 5 5-5" />
                <path d="M5 21h14" />
              </svg>
            </button>
          ) : (
            <span className="w-5 h-5" aria-hidden="true" />
          )}
        </div>
      </div>
    );
  }
);

export const GridUploadInput = React.memo(
  ({ onChange, label, fileInputRef, fileTypeRef }) => {
    const handleTextureSelection = (type) => {
      if (fileInputRef.current) {
        fileInputRef.current.click();
        fileTypeRef.current = type;
      }
    };
    return (
      <>
        <div className="mb-2 flex items-center gap-2">
          {label && <LabelHover label={label} />}
          <div className="relative  flex-[16]">
            <button
              className="w-full bg-[#2B2C2F] text-gray-400 rounded p-1 text-[10px] flex items-center justify-center gap-1"
              onClick={() => handleTextureSelection(label)}
            >
              <img
                className="h-5 w-5"
                src={iconsPath.uploadIcon}
                alt="upload icon"
              />
              <span>Upload</span>
            </button>
          </div>
        </div>
        <input
          type="file"
          ref={fileInputRef}
          onChange={(e) => onChange(e)}
          accept="image/*"
          style={{ display: 'none' }}
        />
      </>
    );
  }
);

const ListForShare = React.memo(({ label, icon, onClick }) => {
  return (
    <div
      onClick={() => {
        if (onClick) {
          onClick();
        }
      }}
      className="flex gap-4 cursor-pointer "
    >
      <div className="p-2 bg-[#454545] rounded-[10px]">
        <img src={icon} alt={label} className="h-6 w-6" />
      </div>
      <div className="flex justify-center items-center">{label}</div>
    </div>
  );
});

export const SubMenuPortal = ({ children, position }) => {
  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        zIndex: 10000,
      }}
    >
      {children}
    </div>,
    document.body
  );
};

export const CustomDropdownWithLabel = ({
  label,
  value,
  onChange,
  options = [],
  placeholder = 'Select an option',
  icon = null,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const selectedOption = options.find((opt) => opt.value === value);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="mb-2">
      <label className="block text-gray-400 text-[10px] mb-1">{label}</label>

      <div className="relative" ref={dropdownRef}>
        {/* Trigger */}
        <div
          className={`w-full bg-[#131414] rounded p-1 pr-6 text-[10px] text-gray-400 ${disabled ? 'opacity-50 pointer-events-none' : 'cursor-pointer'
            } truncate`}
          onClick={() => !disabled && setIsOpen((prev) => !prev)}
        >
          {selectedOption?.label || placeholder}
          {icon && (
            <div className="absolute inset-y-0 right-1 flex items-center pointer-events-none">
              <img src={icon} alt="dropdown icon" />
            </div>
          )}
        </div>

        {/* Dropdown list */}
        {isOpen && (
          <ul className="absolute left-0 z-50 mt-1 w-full bg-[#2B2C2F] border border-[#444] rounded shadow text-gray-300 max-h-40 overflow-y-auto">
            {options.length > 0 ? (
              options.map((opt) => (
                <li
                  key={opt.value}
                  className={`px-2 py-1 text-[10px] hover:bg-[#3F3F3F] ${opt.disabled
                      ? 'opacity-40 cursor-not-allowed'
                      : 'cursor-pointer'
                    }`}
                  onClick={(e) => {
                    e.stopPropagation(); // ✅ prevent dropdown toggle
                    if (!opt.disabled) {
                      onChange({ target: { value: opt.value } });
                      setIsOpen(false); // ✅ close dropdown
                    }
                  }}
                >
                  {opt.label}
                </li>
              ))
            ) : (
              <div className="w-full text-center py-1 text-[10px]">
                No Data available
              </div>
            )}
          </ul>
        )}
      </div>
    </div>
  );
};
